type LastKnownPrice = { price: number; at: string };

export type AmazonPriceInput =
  | {
      latestPrice?: number | null;
      lastKnownPrice?: LastKnownPrice | null;
      /** Keepa で取った価格（SP-API で価格が取れなかった商品だけバッチが入れる） */
      keepaProduct?: {
        latestPrice?: number | null;
        lastKnownPrice?: LastKnownPrice | null;
        keepaUpdatedAt?: string | null;
      } | null;
    }
  | null
  | undefined;

export type AmazonPricePick = {
  /** 利益の計算に使う Amazon 価格（分からない時は -1） */
  price: number;
  /** current=今の出品価格（Amazon と Keepa の新しい方。画面では区別しない） / last=出品者なしなので最後に価格があった時の値（参考） / none=分からない */
  /** stale = 1 年より古い値しか無い（間違った値を出すより出さない。2026-10-17 ユーザー指定） */
  source: 'current' | 'last' | 'none' | 'stale';
  /** source が last の時だけ: その価格があった日（「9/12」の形。サーバーの "2026/9/12 10:00:00" から作る） */
  at?: string;
};

/** "2026/9/12 10:00:00" → "9/12"。読めない形ならそのまま返す */
export const toMonthDay = (at: string): string => {
  const m = /^\d{4}\/(\d{1,2})\/(\d{1,2})/.exec(at);
  return m ? `${m[1]}/${m[2]}` : at;
};

/** "2026/9/12 10:00:00" を比較用の数値にする。読めなければ null（ゼロ埋めが無いので文字列のままでは比べられない） */
const toTime = (at: string | null | undefined): number | null => {
  const m = /^(\d{4})\/(\d{1,2})\/(\d{1,2}) (\d{1,2}):(\d{2}):(\d{2})/.exec(at ?? '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime() : null;
};

/** 価格のある方。両方あれば日時の新しい方 */
const newerLastKnown = (a: LastKnownPrice | null | undefined, b: LastKnownPrice | null | undefined) => {
  if (!a || a.price <= 0) return b?.price > 0 ? b : null;
  if (!b || b.price <= 0) return a;
  return (toTime(b.at) ?? 0) > (toTime(a.at) ?? 0) ? b : a;
};

/**
 * 利益の計算に使う Amazon 価格を決める。Amazon（SP-API）と Keepa のうち新しい方を使う。
 * - Amazon の今の価格は lastKnownPrice.at の時点のもの（SP-API で取るたびに履歴が残るため）
 * - Keepa の今の価格は keepaUpdatedAt の時点のもの
 * 今の価格がどちらにも無ければ、最後に価格があった時の値のうち新しい方を参考として使う。
 */
/** これより古い値は「無い」ものとして扱う（1 年。2026-10-17 ユーザー指定） */
export const MAX_PRICE_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export const pickAmazonPrice = (ap: AmazonPriceInput, now: number = Date.now()): AmazonPricePick => {
  const kp = ap?.keepaProduct;
  const keepaAt = toTime(kp?.keepaUpdatedAt);
  const amazonAtRaw = toTime(ap?.lastKnownPrice?.at);
  // Amazon（SP-API）の値が 1 年より古ければ、今の価格としても最終価格としても使わない
  const amazonTooOld = amazonAtRaw != null && now - amazonAtRaw > MAX_PRICE_AGE_MS;
  const current = amazonTooOld ? -1 : (ap?.latestPrice ?? -1);
  const amazonAt = amazonTooOld ? null : amazonAtRaw;
  // Keepa の方が新しい = SP-API で価格が更新されていない。Amazon の価格は古いので今の価格として使わない
  const keepaNewer = keepaAt != null && (amazonAt == null || keepaAt > amazonAt);

  if (current > 0 && !keepaNewer) {
    return { price: current, source: 'current' };
  }
  const keepaCurrent = kp?.latestPrice ?? -1;
  if (keepaCurrent > 0 && (keepaNewer || current <= 0)) {
    return { price: keepaCurrent, source: 'current' };
  }
  const last = newerLastKnown(amazonTooOld ? null : ap?.lastKnownPrice, kp?.lastKnownPrice);
  if (last && now - (toTime(last.at) ?? now) <= MAX_PRICE_AGE_MS) {
    return { price: last.price, source: 'last', at: toMonthDay(last.at) };
  }
  if (amazonTooOld || last) {
    return { price: -1, source: 'stale' };
  }
  return { price: -1, source: 'none' };
};
